<template>
    <VAppBar id="app-bar" :height="!store.layoutVertical ? 100 : 64" color="transparent" flat order="1">
        <VCard :class="`w-100 d-flex ${store.layoutVertical ? 'align-center' : ''}`"
            :style="`${backgroundStyle}; ${!store.layoutVertical ? 'height: 100px' : 'height: 64px'}`" flat rounded="0">
            <VContainer :class="`d-flex ${store.layoutVertical ? 'align-center' : ''}`">
                <VBtn v-if="store.layoutVertical" class="hidden-lg-and-up mr-2"
                    @click="link.secureMainDrawer = !link.secureMainDrawer" size="small" icon variant="tonal">
                    <VIcon size="x-large" icon="$menu" />
                </VBtn>

                <AppLogo v-if="!store.layoutVertical" />

                <VSpacer />

                <VBtn color="medium-emphasis" @click="link.themeDrawer = !link.themeDrawer" class="ml-2" icon
                    size="small">
                    <VIcon size="x-large" icon="$mdiTune" />
                </VBtn>

                <AppBarThemeToggle class="ml-2" />
                <SecureAccountMenu />
            </VContainer>
        </VCard>

        <!-- Buttons row (replaces VTabs) -->
        <template v-if="!store.layoutVertical" #extension>
            <VContainer class="d-flex align-center pa-0"
                :style="`${!store.layoutVertical ? 'margin-bottom: 96px' : ''}`">
                <div class="d-flex align-center ga-2 w-100 px-2">
                    <!-- Normal buttons (Dashboard/Orders/etc) -->
                    <template v-for="tab in navButtons" :key="tab.id">
                        <!-- Videos dropdown button -->
                        <VMenu open-on-hover location="bottom"
                            @update:modelValue="(open) => open && modulesStore.fetchModules()"
                            v-if="tab.to === '/videos'" :close-on-content-click="true">
                            <template #activator="{ props }">
                                <VBtn v-bind="props" :prepend-icon="tab.icon" size="small"
                                    :variant="isVideosRoute ? 'tonal' : 'text'"
                                    :color="isVideosRoute ? 'primary' : 'medium-emphasis'" class="text-capitalise"
                                    @click.stop="goTo('/videos')">
                                    {{ tab.text }}
                                    <VIcon class="ml-1" icon="$mdiChevronDown" size="18" />
                                </VBtn>
                            </template>

                            <VList density="compact" min-width="320" :style="backgroundStyle">
                                <!-- All videos (no query) -->
                                <VListItem exact :to="{ path: '/videos' }" title="All videos">
                                    <template #prepend>
                                        <VIcon icon="$mdiPlayBoxMultipleOutline" color="primary" />
                                    </template>
                                </VListItem>

                                <VDivider />

                                <!-- Module filters (exact match on query) -->
                                <VListItem v-for="s in moduleSubItems" :key="s.id" exact :to="s.to">
                                    <template #prepend>
                                        <VIcon icon="$mdiPlayCircleOutline" color="primary" />
                                    </template>
                                    <template v-if="s.text">
                                        <div class="text-body-1 font-weight-medium">
                                            {{ s.text }}
                                        </div>
                                    </template>
                                    <template v-if="s.subText">
                                        <div class="text-body-2 text-medium-emphasis">
                                            {{ s.subText }}
                                        </div>
                                    </template>
                                </VListItem>
                            </VList>
                        </VMenu>

                        <!-- Normal route buttons -->
                        <VBtn v-else :to="tab.to" :prepend-icon="tab.icon" size="small"
                            :variant="isActive(tab.to) ? 'tonal' : 'text'"
                            :color="isActive(tab.to) ? 'primary' : 'medium-emphasis'" class="text-capitalise">
                            {{ tab.text }}
                        </VBtn>
                    </template>
                </div>
            </VContainer>
        </template>
    </VAppBar>
</template>

<script setup lang="ts">
import AppLogo from '../Logo/AppLogo.vue'
import AppBarThemeToggle from '../Theme/ToggleTheme.vue'
import SecureAccountMenu from './AppBar/SecureAccountMenu.vue'

import { useAppStore } from '@S/appStore'
import { useAppLink } from '@S/appLink'
import { useRoute, useRouter } from 'vue-router'
import { useBackgroundStyle } from '@R/constants/componentStyle'
import { secureAppBarRoutes } from '@U/navigationGroups'
import { useVideoModulesStore } from '@S/videoModulesStore'

type AppBarTab = {
    id: number
    text: string
    to: string
    icon?: string
    style?: string
}

const store = useAppStore()
const link = useAppLink()
const route = useRoute()
const router = useRouter()
const backgroundStyle = useBackgroundStyle()

const modulesStore = useVideoModulesStore()

const isVideosRoute = computed(() => route.path.startsWith('/videos'))

const isActive = (to: string) => {
    // Treat "/videos/anything" as active for "/videos" button.
    if (to === '/videos') return route.path.startsWith('/videos')
    return route.path === to
}

const goTo = async (to: string) => {
    if (route.fullPath === to) return
    await router.push(to)
}

onMounted(async () => {
    await nextTick()
    await modulesStore.fetchModules()
})

const navButtons = computed<AppBarTab[]>(() => secureAppBarRoutes as AppBarTab[])

const moduleSubItems = computed(() =>
    (modulesStore.modules || []).map((m) => ({
        id: 9000 + m.id,
        text: m.title,
        to: { path: '/videos', query: { module: m.slug } },
        subText: m.description || undefined,
    })),
)
</script>
